import { Moon, Sun } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface ThemeToggleProps {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'button' | 'icon';
  className?: string;
}

/**
 * ThemeToggle component for switching between light and dark themes
 */
export function ThemeToggle({ 
  size = 'md', 
  variant = 'button',
  className = '' 
}: ThemeToggleProps) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  // Size configurations
  const sizes = {
    sm: {
      button: 'px-2 py-1 text-xs',
      icon: 'p-1',
      iconSize: 14,
    },
    md: {
      button: 'px-3 py-2 text-sm',
      icon: 'p-2',
      iconSize: 16,
    },
    lg: {
      button: 'px-4 py-2 text-base',
      icon: 'p-2.5',
      iconSize: 20,
    },
  };

  const sizeConfig = sizes[size];

  if (variant === 'icon') {
    return (
      <button
        onClick={toggleTheme}
        className={`
          ${sizeConfig.icon}
          rounded-lg
          text-gray-500 dark:text-gray-400
          hover:text-gray-700 dark:hover:text-gray-200
          hover:bg-gray-100 dark:hover:bg-gray-700
          transition-colors
          focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
          ${className}
        `}
        title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
        aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      >
        {isDark ? (
          <Sun size={sizeConfig.iconSize} />
        ) : (
          <Moon size={sizeConfig.iconSize} />
        )}
      </button>
    );
  }

  return (
    <button
      onClick={toggleTheme}
      className={`
        ${sizeConfig.button}
        inline-flex items-center gap-2
        rounded-lg border
        border-gray-300 dark:border-gray-600
        bg-white dark:bg-gray-800
        text-gray-700 dark:text-gray-200
        hover:bg-gray-50 dark:hover:bg-gray-700
        focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
        transition-colors
        font-medium
        ${className}
      `}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      {isDark ? (
        <>
          <Sun size={sizeConfig.iconSize} />
          Light Mode
        </>
      ) : (
        <>
          <Moon size={sizeConfig.iconSize} />
          Dark Mode
        </>
      )}
    </button>
  );
}

/**
 * Compact theme toggle for use in navigation bars
 */
export function CompactThemeToggle({ className = '' }: { className?: string }) {
  return (
    <ThemeToggle 
      variant="icon" 
      size="md" 
      className={className}
    />
  );
} 